import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { CamelCasedPropertiesDeep } from "type-fest";
import { recursiveCamelCaseCipherTypesafe } from "../../postgrestApi";
import FetchClients, { ClientOutput } from "../client";
import {
  BackpackServiceError,
  isBackpackResponseErrorBody,
  throwBackpackResponseError,
} from "../errors";

export const PHOTO_EXPORT_PREVIEW_QUERY_KEY = "equipment-photo-export-preview";
export const PHOTO_EXPORT_MAX_WAIT_MS = 3 * 60 * 60_000;

export const PHOTO_EXPORT_INTERNAL_CODES = {
  invalidSite: "equipment/photo-export-invalid-site",
  invalidEquipment: "equipment/photo-export-invalid-equipment",
  tooLarge: "equipment/photo-export-too-large",
  inProgress: "equipment/photo-export-in-progress",
  exportForbidden: "equipment/export-forbidden",
  exportNotFound: "equipment/export-not-found",
} as const;

export class PhotoExportApiError extends Error {
  status?: number;
  internalCode?: string;

  constructor({
    message,
    status,
    internalCode,
  }: {
    message: string;
    status?: number;
    internalCode?: string;
  }) {
    super(message);
    this.name = "PhotoExportApiError";
    this.status = status;
    this.internalCode = internalCode;
  }
}

export type PhotoExportPreview = CamelCasedPropertiesDeep<
  ClientOutput["equipment"]["PhotoExportPreviewResponse"]
>;
export type PhotoExportEquipment = PhotoExportPreview["equipment"][number];

const photoExport = {
  usePreview,
  useCreatePhotoExport,
};

export default photoExport;

function isPositiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function assertValidSiteId(siteId: number) {
  if (!isPositiveInteger(siteId)) {
    throw new PhotoExportApiError({
      message: "Invalid site.",
      status: 400,
      internalCode: PHOTO_EXPORT_INTERNAL_CODES.invalidSite,
    });
  }
}

export function uniquePositiveEquipmentIds(ids: number[]): number[] {
  return [...new Set(ids.filter(isPositiveInteger))];
}

function isValidPreviewEquipment(row: PhotoExportEquipment): boolean {
  return (
    isPositiveInteger(row.equipmentId) &&
    typeof row.name === "string" &&
    typeof row.equipmentType === "string" &&
    Number.isFinite(row.photoCount) &&
    row.photoCount >= 0 &&
    Number.isFinite(row.estimatedBytes) &&
    row.estimatedBytes >= 0
  );
}

function normalizePreview(body: PhotoExportPreview): PhotoExportPreview {
  const equipment = (body.equipment ?? []).filter(isValidPreviewEquipment);
  const maxSourceBytes =
    Number.isFinite(body.maxSourceBytes) && body.maxSourceBytes > 0
      ? body.maxSourceBytes
      : 4294967296;

  return {
    maxSourceBytes,
    photoCount: Number.isFinite(body.photoCount) ? body.photoCount : 0,
    estimatedBytes: Number.isFinite(body.estimatedBytes)
      ? body.estimatedBytes
      : 0,
    overLimit: Boolean(body.overLimit),
    equipment,
  };
}

function throwPhotoExportCreateError(error: unknown) {
  if (isBackpackResponseErrorBody(error)) {
    if (error.error.internal_code === PHOTO_EXPORT_INTERNAL_CODES.inProgress) {
      throw new PhotoExportApiError({
        message: error.error.message || "An export is already running.",
        status: error.error.code ?? 429,
        internalCode: PHOTO_EXPORT_INTERNAL_CODES.inProgress,
      });
    }
    if (error.error.internal_code === PHOTO_EXPORT_INTERNAL_CODES.tooLarge) {
      throw new PhotoExportApiError({
        message:
          error.error.message ||
          "The file size cannot exceed 4 GB. Remove equipment until the estimated size is under 4 GB.",
        status: error.error.code ?? 413,
        internalCode: PHOTO_EXPORT_INTERNAL_CODES.tooLarge,
      });
    }
  }
  throwBackpackResponseError(error);
}

function getPhotoExportErrorStatus(error: unknown): number | undefined {
  if (error instanceof PhotoExportApiError) {
    return error.status;
  }
  if (error instanceof BackpackServiceError) {
    return error.code;
  }
  return undefined;
}

const getPhotoExportPreview = async (
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<PhotoExportPreview> => {
  assertValidSiteId(siteId);

  const { data, error } = await FetchClients.equipment.GET(
    "/equipment/site/{site_id}/photo-export-preview",
    {
      params: {
        path: { site_id: siteId },
      },
      headers: checkFeatureAccessHeader,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }

  if (!data) {
    throw new PhotoExportApiError({
      message: "Unable to estimate photo export size. Please try again later.",
    });
  }

  return normalizePreview(recursiveCamelCaseCipherTypesafe(data));
};

function usePreview(
  siteId: number,
  { enabled }: { enabled: boolean } = { enabled: true }
) {
  return useQueryWithAccessCheckLog(
    ["Equipment", "read"],
    (checkFeatureAccessHeader) =>
      getPhotoExportPreview(siteId, checkFeatureAccessHeader),
    {
      queryKey: [PHOTO_EXPORT_PREVIEW_QUERY_KEY, siteId],
      enabled: enabled && isPositiveInteger(siteId),
      retry: (failureCount, error) => {
        const status = getPhotoExportErrorStatus(error);
        if (status && status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    }
  );
}

const createPhotoExport = async (
  siteId: number,
  equipmentIds: number[],
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) => {
  assertValidSiteId(siteId);

  const sanitizedIds = uniquePositiveEquipmentIds(equipmentIds);
  if (sanitizedIds.length === 0) {
    throw new PhotoExportApiError({
      message: "Select at least one piece of equipment.",
      status: 400,
      internalCode: PHOTO_EXPORT_INTERNAL_CODES.invalidEquipment,
    });
  }

  const { data, error } = await FetchClients.equipment.POST(
    "/equipment/site/{site_id}/photo-exports",
    {
      params: {
        path: { site_id: siteId },
      },
      body: { equipment_ids: sanitizedIds },
      headers: checkFeatureAccessHeader,
    }
  );

  if (error) {
    throwPhotoExportCreateError(error);
  }

  if (!data?.export_id) {
    throw new PhotoExportApiError({
      message: "Unable to start photo export. Please try again later.",
      status: 500,
    });
  }

  return recursiveCamelCaseCipherTypesafe(data);
};

function useCreatePhotoExport(siteId: number) {
  return useMutationWithAccessCheckLog(
    ["Equipment", "read"],
    (equipmentIds: number[], checkFeatureAccessHeader) =>
      createPhotoExport(siteId, equipmentIds, checkFeatureAccessHeader)
  );
}
